import { scaleLinear, scaleLog } from 'd3-scale';
import type { ScaleLinear } from 'd3-scale';

import { line } from 'd3-shape';
import { format } from 'd3-format';
import * as utils from 'svg-path-reverse';
import type { GraphLimits, GraphOptions, GraphGeometry, GraphPoint, GraphLine } from '../index.ts';

type GraphSegmentArray = GraphLine[];

const LINE_TYPE_INDICES: Record<string, number> = {
  functionLine: 1,
  functionSpurs: 1,
  envelopeUpper: 2,
  envelopeLower: 3,
};

function computeGeometry(limits: GraphLimits, options: GraphOptions, width: number, height: number): GraphGeometry {
  const xscale = options.xscale === 'log' ? scaleLog() : scaleLinear();
  const yscale = options.yscale === 'log' ? scaleLog() : scaleLinear();

  return {
    tickcount: [options.xticks || 5, options.yticks || 5],
    width: width,
    height: height,
    limits: limits,
    xScale: xscale.domain([limits.xmin, limits.xmax]).range([0, width]),
    yScale: yscale.domain([limits.ymin, limits.ymax]).range([height, 0]),
  };
}

function generateEnvelopeFill(line: GraphLine, geometry: GraphGeometry): string {
  if (line.length < 1 || !line[0] || line[0].length !== 4) return '';

  let result = '';
  const lowerPath = 'M ' + utils.normalize(generateEnvelopeLower(line, geometry)).substr(1).replace(/M/g, 'L').trim();
  const upperPath =
    'L' +
    utils
      .reverse('M' + utils.normalize(generateEnvelopeUpper(line, geometry)).substr(1).replace(/M/g, 'L'))
      .trim()
      .substr(1);

  const getLineEndpoints = (svgPath: string) => {
    const initial = { x: 0, y: 0 };
    const final = { x: 0, y: 0 };
    const splitPath = svgPath
      .split(/[A-Z]/)
      .map((pair) => pair.trim())
      .filter((x) => !!x);
    const extractCoordPair = (index: number) => {
      const path = splitPath[index];
      if (path) {
        return path
          .split(' ')
          .map((val) => val.trim())
          .map((val) => +val);
      } else {
        throw 'svgPath endpoints not found';
      }
    };
    if (extractCoordPair(0).length === 2 && extractCoordPair(splitPath.length - 1).length === 2) {
      initial.x = extractCoordPair(0)[0] as number; //Covered by guard clause above
      initial.y = extractCoordPair(0)[1] as number;
      final.x = extractCoordPair(splitPath.length - 1)[0] as number;
      final.y = extractCoordPair(splitPath.length - 1)[1] as number;
    } else {
      throw 'coordinate pair not found';
    }
    return { initial, final };
  };
  const lineEndPoints = {
    initial: { x: 0, y: 0 },
    final: { x: 0, y: 0 },
  };
  let lowerEndPoints;
  let upperEndPoints;
  try {
    lowerEndPoints = lowerPath !== 'M ' ? getLineEndpoints(lowerPath) : lineEndPoints;
    upperEndPoints = upperPath.indexOf('undefined') === -1 ? getLineEndpoints(upperPath) : lineEndPoints;
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  } catch (e) {
    return '';
  }
  const { width: xMax, height: yMax } = geometry;

  let rightEdgePath = ' ';
  if (lowerEndPoints.final.x < upperEndPoints.initial.x && lowerEndPoints.final.y > upperEndPoints.initial.y) {
    //bottom right corner
    rightEdgePath += `L ${xMax} ${yMax} `;
    rightEdgePath += `L ${xMax} ${upperEndPoints.initial.y} `;
  } else if (lowerEndPoints.final.x > upperEndPoints.initial.x && lowerEndPoints.final.y > upperEndPoints.initial.y) {
    //top right corner
    rightEdgePath += `L ${xMax} ${lowerEndPoints.final.y} `;
    rightEdgePath += `L ${xMax} 0 `;
  }

  let leftEdgePath = ' ';
  if (upperEndPoints.final.x > lowerEndPoints.initial.x && upperEndPoints.final.y < lowerEndPoints.initial.y) {
    //top left corner
    leftEdgePath += `L 0 0 `;
  } else if (upperEndPoints.final.x < lowerEndPoints.initial.x && upperEndPoints.final.y < lowerEndPoints.initial.y) {
    //bottom left corner
    leftEdgePath += `L 0 ${yMax} `;
    leftEdgePath += `L ${lowerEndPoints.initial.x} ${lowerEndPoints.initial.y}`;
  }

  result += lowerPath + rightEdgePath + upperPath + leftEdgePath;
  return result;
}

function generateEnvelopeLower(line: GraphLine, geometry: GraphGeometry): string {
  return generateLine('envelopeLower', line, geometry);
}

function generateEnvelopeUpper(line: GraphLine, geometry: GraphGeometry): string {
  return generateLine('envelopeUpper', line, geometry);
}

function generateFunctionLine(line: GraphLine, geometry: GraphGeometry): string {
  return generateLine('functionLine', line, geometry);
}

function generateFunctionSpurs(line: GraphLine, geometry: GraphGeometry): string {
  return generateLine('functionSpurs', line, geometry);
}

function generateLine(type: string, line: GraphLine, geometry: GraphGeometry): string {
  let svgPath = '';
  if (line && line[0]) {
    if (type in ['envelopeUpper', 'envelopeLower'] && line[0].length !== 4) return '';

    //const { data, limits } = model;
    const yCoord = LINE_TYPE_INDICES[type] ?? 1;
    const segments =
      type === 'functionSpurs'
        ? getSpurSegments(line, geometry.limits, yCoord)
        : getSegments(line, geometry.limits, yCoord);
    if (segments.length) {
      // [x, y1, y2, y3, ...] for offset lines
      const { xScale, yScale } = geometry;
      let segmentSVGPath = '';
      segments.forEach((segment) => {
        segmentSVGPath = d3Line(0, yCoord, xScale, yScale, segment) || '';
        svgPath += segmentSVGPath;
      });
    }
  }
  return svgPath;
}

function getSpurSegments(graphArray: GraphLine, limits: GraphLimits, yIndex: number): GraphSegmentArray {
  const segments: GraphSegmentArray = [];
  graphArray.forEach((currentPoint) => {
    const x = currentPoint[0] as number;
    const y = currentPoint[yIndex] as number;
    if (x >= limits.xmin && x <= limits.xmax && y >= limits.ymin) {
      segments.push([
        [x, limits.ymin],
        [x, Math.min(y, limits.ymax)],
      ]);
    }
  });
  return segments;
}

function getSegments(graphArray: GraphLine, limits: GraphLimits, yIndex: number): GraphSegmentArray {
  const segments: GraphSegmentArray = [];
  let segment: GraphLine = [];
  let previousPoint: GraphPoint = [];

  graphArray.forEach((currentPoint, i) => {
    const currentInZone = isPointInZone(currentPoint, limits, yIndex);
    // No previous value comes into play for the first point - it's either in or out.
    if (i === 0 && currentInZone) {
      segment.push(currentPoint);
    }
    if (i > 0) {
      const previousInZone = isPointInZone(previousPoint, limits, yIndex);
      if (previousInZone && currentInZone) {
        segment.push(currentPoint);
      } else if (!previousInZone && currentInZone) {
        segment = [];
        const cept = intercept(previousPoint, currentPoint, limits, yIndex);
        if (cept[0]) {
          segment.push(cept[0]);
        }
        segment.push(currentPoint);
      } else if (previousInZone && !currentInZone) {
        const cept = intercept(previousPoint, currentPoint, limits, yIndex);
        if (cept[0]) {
          segment.push(cept[0]);
        }
        segments.push(segment);
        segment = [];
      } else {
        // neither are in zone, but the line between them might do a through-and-through
        const points = intercept(previousPoint, currentPoint, limits, yIndex);
        if (points.length === 2 && points[0] && points[1]) {
          segment.push(points[0]);
          segment.push(points[1]);
          segments.push(segment);
          segment = [];
        }
      }
    }
    previousPoint = currentPoint;
  });

  if (segment && segment.length > 0) {
    segments.push(segment);
  }

  return segments;
}

function isPointInZone(currentPoint: number[], limits: GraphLimits, yIndex: number): boolean {
  const [x, y] = [currentPoint[0] as number, currentPoint[yIndex] as number]; //Covered by guard clause from caller
  return x >= limits.xmin && x <= limits.xmax && y >= limits.ymin && y <= limits.ymax;
}

function intercept(previousPoint: number[], currentPoint: number[], limits: GraphLimits, yIndex: number): number[][] {
  const [x1, y1] = [previousPoint[0] as number, previousPoint[yIndex] as number]; //Covered by guard clause from caller
  const [x2, y2] = [currentPoint[0] as number, currentPoint[yIndex] as number];
  const m = (y2 - y1) / (x2 - x1);
  const b = y1 - m * x1;

  const result = [];

  // Get y boundary intercepts between the two points
  const yLimits = [];
  if (m > 0) {
    // Do min before max is slope is positive, so we get increasing x order
    if ((y1 < limits.ymin && y2 >= limits.ymin) || (y2 < limits.ymin && y1 > limits.ymin)) {
      yLimits.push(limits.ymin);
    }
    if ((y1 > limits.ymax && y2 <= limits.ymax) || (y2 > limits.ymax && y1 < limits.ymax)) {
      yLimits.push(limits.ymax);
    }
  } else {
    // Do max before min is slope is negative, so we get increasing x order
    if ((y1 > limits.ymax && y2 <= limits.ymax) || (y2 > limits.ymax && y1 < limits.ymax)) {
      yLimits.push(limits.ymax);
    }
    if ((y1 < limits.ymin && y2 >= limits.ymin) || (y2 < limits.ymin && y1 > limits.ymin)) {
      yLimits.push(limits.ymin);
    }
  }
  for (const yLimit of yLimits) {
    const x3 = x2 === x1 ? x2 : (yLimit - b) / m; // Special case for vertical lines where m is NaN
    if (x3 > limits.xmin && x3 < limits.xmax && yLimit !== y2 && yLimit !== y1) {
      const point = [x3, 0, 0, 0];
      point[yIndex] = yLimit;
      result.push(point);
    }
  }

  // Get x boundary intercepts between the two points
  const xLimits = [];
  if ((x1 < limits.xmin && x2 >= limits.xmin) || (x2 < limits.xmin && x1 > limits.xmin)) {
    xLimits.push(limits.xmin);
  }
  if ((x1 > limits.xmax && x2 <= limits.xmax) || (x2 > limits.xmax && x1 < limits.xmax)) {
    xLimits.push(limits.xmax);
  }
  for (const xLimit of xLimits) {
    const y3 = m * xLimit + b;
    if (y3 > limits.ymin && y3 < limits.ymax && xLimit !== x2 && xLimit !== x1) {
      const point = [xLimit, 0, 0, 0];
      point[yIndex] = y3;
      result.push(point);
    }
  }
  return result;
}

function d3Line(
  xIndex: number,
  yIndex: number,
  xScale: (domainValue: number) => number,
  yScale: (domainValue: number) => number,
  segment: GraphLine,
): string | null {
  // The typescript "as [number,number][]" is a hack to work around inaccurate typing in D3 -
  // The line.x() and line.y() accessors are typed to expect a number pair for their first argument.
  // In truth, that first argument is allowed to be anything the body of our accessor function can find the value in.
  // What we deliver to d is a single value in our segment array, which has a single x and multiple y values.
  // We pick the x and y values we need using the index values we receive from the caller.
  // There may be an overload that lets us speak the truth but I haven't found it.
  return line()
    .x((d: GraphPoint) => {
      return xScale(d[xIndex] as number);
    })
    .y((d: GraphPoint) => {
      return yScale(d[yIndex] as number);
    })(segment as [number, number][]);
}

function tickPositions(
  min: number,
  max: number,
  ticks: number | number[] | undefined,
  scaleString: string | undefined,
  formatString: string | undefined,
  scaler: ScaleLinear<number, number, never>,
  count: number,
): { value: string; coordinate: number }[] {
  let result = null;
  const formatter = formatString ? format(formatString) : scaleString === 'log' ? format('.2s') : format('.1f');
  const logFormatter = function logFormatter(
    tick: number,
    labelsPerDecade: number,
    formatfn: (n: number | { valueOf(): number }) => string,
  ) {
    const x = Math.log(tick) / Math.log(10) + 1e-6;
    const fraction = Math.abs(x - Math.floor(x));
    const draw =
      labelsPerDecade === 9 ||
      (labelsPerDecade === 5 && fraction < 0.7) ||
      (labelsPerDecade === 3 && (fraction < 0.31 || (fraction > 0.61 && fraction < 0.7))) ||
      (labelsPerDecade === 1 && fraction < 0.1);
    return draw ? formatfn(tick) : '';
  };
  const ticksArray =
    ticks instanceof Array
      ? ticks.filter((tick) => tick <= max && tick >= min)
      : scaler.ticks(scaleString === 'log' ? undefined : ticks);
  result = ticksArray.map((tick: number) => {
    const tickScale = scaler(tick); // could return undefined
    return {
      value: scaleString === 'log' ? logFormatter(tick, count, formatter) : formatter(tick),
      coordinate: tickScale ? tickScale : 0,
    };
  });
  return result;
}

export {
  computeGeometry,
  generateEnvelopeFill,
  generateEnvelopeLower,
  generateEnvelopeUpper,
  generateFunctionLine,
  generateFunctionSpurs,
  tickPositions,
  getSpurSegments,
  getSegments,
};
